import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Brain, Sparkles, Shield, Compass, Zap, Users,
  LineChart, Briefcase, Handshake, Building2, Lock, ChevronDown,
} from 'lucide-react';
import { api } from '../context/AuthContext';

/* ─────────────────────────────────────────────────────────────────
   Inject all CSS once at module load (before first render so
   keyframes are available when components mount)
   ───────────────────────────────────────────────────────────── */
if (typeof document !== 'undefined' && !document.getElementById('modern-landing-css')) {
  const css = `
    @keyframes ml-aurora {
      0%, 100% { transform: translate3d(0,0,0) scale(1); }
      33% { transform: translate3d(-4%, 3%, 0) scale(1.08); }
      66% { transform: translate3d(5%, -2%, 0) scale(0.96); }
    }
    @keyframes ml-aurora-2 {
      0%, 100% { transform: translate3d(0,0,0) scale(1); opacity: 0.55; }
      50% { transform: translate3d(8%, 6%, 0) scale(1.15); opacity: 0.75; }
    }
    @keyframes ml-marquee {
      from { transform: translateX(0); }
      to   { transform: translateX(-50%); }
    }
    @keyframes ml-shine {
      0%   { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes ml-pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.4; transform: scale(0.6); }
    }
    @keyframes ml-rise {
      from { opacity: 0; transform: translateY(32px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes ml-rise-slow {
      from { opacity: 0; transform: translateY(48px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .ml-rise { animation: ml-rise 0.9s cubic-bezier(0.22,1,0.36,1) both; }

    .ml-aurora-1 {
      position: absolute;
      top: -20%; left: -10%;
      width: 70%; height: 80%;
      background: radial-gradient(circle, rgba(199,164,109,0.28), transparent 60%);
      filter: blur(80px);
      animation: ml-aurora 22s ease-in-out infinite;
      pointer-events: none;
    }
    .ml-aurora-2 {
      position: absolute;
      top: 10%; right: -15%;
      width: 60%; height: 70%;
      background: radial-gradient(circle, rgba(184,108,52,0.18), transparent 60%);
      filter: blur(90px);
      animation: ml-aurora-2 28s ease-in-out infinite;
      pointer-events: none;
    }
    .ml-aurora-3 {
      position: absolute;
      bottom: -10%; left: 30%;
      width: 50%; height: 60%;
      background: radial-gradient(circle, rgba(64,108,148,0.14), transparent 65%);
      filter: blur(100px);
      animation: ml-aurora 32s ease-in-out infinite reverse;
      pointer-events: none;
    }

    .ml-grain {
      position: absolute; inset: 0;
      background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2' seed='3'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/></svg>");
      mix-blend-mode: overlay;
      opacity: 0.18;
      pointer-events: none;
    }

    .ml-grid-bg {
      position: absolute; inset: 0;
      background-image:
        linear-gradient(rgba(199,164,109,0.045) 1px, transparent 1px),
        linear-gradient(90deg, rgba(199,164,109,0.045) 1px, transparent 1px);
      background-size: 56px 56px;
      mask-image: radial-gradient(ellipse 80% 60% at 50% 30%, black 30%, transparent 80%);
      pointer-events: none;
    }

    .ml-gold-text {
      background: linear-gradient(135deg, #E8C896 0%, #C7A46D 45%, #9A7A47 100%);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
    }

    .ml-glass {
      background: linear-gradient(155deg, rgba(20,18,16,0.7), rgba(10,9,8,0.55));
      border: 1px solid rgba(255,255,255,0.06);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      box-shadow: 0 24px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04);
    }

    .ml-card {
      position: relative;
      background: linear-gradient(155deg, rgba(20,18,16,0.85), rgba(10,9,8,0.92));
      border: 1px solid rgba(255,255,255,0.06);
      border-radius: 20px;
      transition: transform 0.4s cubic-bezier(0.22,1,0.36,1), border-color 0.4s, box-shadow 0.4s;
      overflow: hidden;
    }
    .ml-card::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 20px;
      padding: 1px;
      background: linear-gradient(135deg, rgba(199,164,109,0.5), transparent 50%, rgba(199,164,109,0.2));
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
              mask-composite: exclude;
      opacity: 0;
      transition: opacity 0.4s;
      pointer-events: none;
    }
    .ml-card:hover {
      transform: translateY(-6px);
      border-color: rgba(199,164,109,0.18);
      box-shadow: 0 32px 96px rgba(0,0,0,0.55), 0 0 0 1px rgba(199,164,109,0.06);
    }
    .ml-card:hover::before { opacity: 1; }

    .ml-cta {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 17px 30px;
      border-radius: 100px;
      background: linear-gradient(135deg, #E8C896, #C7A46D 60%, #9A7A47);
      color: #0A0807;
      font-family: var(--font-label);
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      text-decoration: none;
      transition: transform 0.25s cubic-bezier(0.22,1,0.36,1), box-shadow 0.25s;
      box-shadow: 0 18px 40px rgba(199,164,109,0.18), inset 0 1px 0 rgba(255,255,255,0.4);
      overflow: hidden;
    }
    .ml-cta::after {
      content: '';
      position: absolute; inset: 0;
      background: linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.5) 50%, transparent 70%);
      transform: translateX(-150%);
      transition: transform 0.7s cubic-bezier(0.22,1,0.36,1);
    }
    .ml-cta:hover {
      transform: translateY(-2px);
      box-shadow: 0 22px 52px rgba(199,164,109,0.32), inset 0 1px 0 rgba(255,255,255,0.5);
    }
    .ml-cta:hover::after { transform: translateX(150%); }

    .ml-cta-ghost {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 17px 26px;
      border-radius: 100px;
      border: 1px solid rgba(255,255,255,0.12);
      background: rgba(255,255,255,0.02);
      color: rgba(255,255,255,0.9);
      font-family: var(--font-label);
      font-size: 13px;
      font-weight: 500;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      text-decoration: none;
      transition: border-color 0.25s, color 0.25s, background 0.25s;
    }
    .ml-cta-ghost:hover {
      border-color: rgba(199,164,109,0.4);
      color: #C7A46D;
      background: rgba(199,164,109,0.04);
    }

    .ml-marquee {
      display: flex;
      width: max-content;
      animation: ml-marquee 40s linear infinite;
    }

    .ml-pill {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 8px 16px;
      border-radius: 100px;
      border: 1px solid rgba(199,164,109,0.25);
      background: rgba(199,164,109,0.05);
      color: #E8C896;
      font-family: var(--font-label);
      font-size: 11px;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      backdrop-filter: blur(12px);
    }
    .ml-pill-dot {
      width: 6px; height: 6px;
      border-radius: 50%;
      background: #C7A46D;
      box-shadow: 0 0 0 4px rgba(199,164,109,0.18);
      animation: ml-pulse 2.4s ease-in-out infinite;
    }

    .ml-tab {
      position: relative;
      padding: 14px 22px;
      border-radius: 12px;
      cursor: pointer;
      font-family: var(--font-label);
      font-size: 13px;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: rgba(255,255,255,0.5);
      border: 1px solid transparent;
      transition: color 0.25s, border-color 0.25s, background 0.25s;
      background: transparent;
    }
    .ml-tab:hover { color: rgba(255,255,255,0.85); }
    .ml-tab.is-active {
      color: #E8C896;
      border-color: rgba(199,164,109,0.25);
      background: rgba(199,164,109,0.06);
    }

    .ml-shot {
      position: relative;
      border-radius: 18px;
      overflow: hidden;
      border: 1px solid rgba(199,164,109,0.18);
      background: linear-gradient(155deg, rgba(20,18,16,0.95), rgba(8,8,10,0.95));
      box-shadow: 0 40px 100px rgba(0,0,0,0.6), inset 0 1px 0 rgba(199,164,109,0.06);
    }
    .ml-shot::after {
      content: '';
      position: absolute; inset: 0;
      background: linear-gradient(180deg, transparent 65%, rgba(8,7,6,0.6) 100%);
      pointer-events: none;
    }
    .ml-shot img { display: block; width: 100%; height: auto; }

    .ml-reveal {
      opacity: 0;
      transform: translateY(40px);
      transition: opacity 0.9s cubic-bezier(0.22,1,0.36,1), transform 0.9s cubic-bezier(0.22,1,0.36,1);
    }
    .ml-reveal.is-visible { opacity: 1; transform: translateY(0); }

    .ml-num-display {
      font-family: var(--font-display);
      font-feature-settings: "tnum" 1;
      font-variant-numeric: tabular-nums;
    }

    @media (max-width: 768px) {
      .ml-hero-grid { grid-template-columns: 1fr !important; }
      .ml-hero-visual { display: none !important; }
    }
  `;
  const tag = document.createElement('style');
  tag.id = 'modern-landing-css';
  tag.textContent = css;
  document.head.appendChild(tag);
}

/* ─────────────────────────────────────────────────────────────────
   Hooks
   ───────────────────────────────────────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setSeen(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, seen];
}

function Reveal({ children, delay = 0, className = '', as: Tag = 'div', ...rest }) {
  const [ref, seen] = useInView();
  return (
    <Tag
      ref={ref}
      className={`ml-reveal ${seen ? 'is-visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

function CountUp({ value, suffix = '', duration = 1400 }) {
  const [ref, seen] = useInView(0.4);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!seen) return;
    const target = parseFloat(value);
    if (isNaN(target)) return;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.round(target * eased));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [seen, value, duration]);
  return <span ref={ref}>{n}{suffix}</span>;
}

/* ─────────────────────────────────────────────────────────────────
   Data
   ───────────────────────────────────────────────────────────── */
const SURFACES = [
  { icon: Handshake, label: 'Cofounders', body: 'Match on Arc, skill, and execution signal, not just a job title.', tag: 'PEOPLE' },
  { icon: Users,     label: 'Skilled talent', body: 'Find builders who can close product, ops, technical, and commercial gaps.', tag: 'TEAM' },
  { icon: LineChart, label: 'Angel capital', body: 'Reach angels through structured requests built around venture context.', tag: 'CAPITAL' },
  { icon: Briefcase, label: 'Live ventures', body: 'Review startups actively raising or hiring, with founding team Arc data.', tag: 'DEALS' },
];

const AUDIENCES = [
  {
    key: 'founders',
    label: 'Founders',
    icon: Zap,
    title: 'Find the people your startup is missing.',
    body: 'Use Arc data, skills, and intent to find cofounders, talent, and angels who match the next gap in the business.',
    bullets: ['Cofounder match', 'Senior hire pipeline', 'Angel intros'],
  },
  {
    key: 'talent',
    label: 'Skilled talent',
    icon: Users,
    title: 'Find ventures worth joining.',
    body: 'Show how you think, build, decide, and execute so founders can see where you create leverage, before the resume.',
    bullets: ['Arc-verified profile', 'Inbound from founders', 'Equity & cash roles'],
  },
  {
    key: 'angels',
    label: 'Angels',
    icon: LineChart,
    title: 'Discover ventures with stronger signal.',
    body: 'Look beyond pitch-deck claims and review founders, teams, traction, and Arc-informed working signals.',
    bullets: ['Verified founders', 'Deal flow filter', 'Team Arc context'],
  },
  {
    key: 'businesses',
    label: 'Businesses',
    icon: Building2,
    title: 'Find skilled people for real gaps.',
    body: 'Discover people whose working style, skills, and availability match the capability you actually need.',
    bullets: ['Skill-matched talent', 'Fractional roles', 'Project specialists'],
  },
];

const STEPS = [
  { n: '01', label: 'Take the Brain Scan', body: '60 questions. About 8 minutes. We map how you build, decide, and operate under pressure.', icon: Brain },
  { n: '02', label: 'Get your Arc profile', body: 'Your Engine and Arc become a practical operating signal on your account, visible to the people you choose.', icon: Compass },
  { n: '03', label: 'Move into the Network', body: 'Browse founders, ventures, talent and angels. Send focused intro requests with context built in.', icon: Sparkles },
];

const STATS = [
  { n: 6,   label: 'Core Engines' },
  { n: 30,  label: 'Arc Types' },
  { n: 60,  label: 'Brain Scan prompts' },
  { n: 100, label: 'Free verifications' },
];

const ENGINES = [
  { name: 'Titan',     trait: 'Force, momentum, will',          glyph: '◆' },
  { name: 'Arbiter',   trait: 'Judgment, clarity, structure',   glyph: '◈' },
  { name: 'Alchemist', trait: 'Transformation, invention',      glyph: '✦' },
  { name: 'Artisan',   trait: 'Craft, taste, expression',       glyph: '✧' },
  { name: 'Oracle',    trait: 'Insight, foresight, pattern',    glyph: '◉' },
  { name: 'Sovereign', trait: 'Vision, sovereignty, command',   glyph: '⬟' },
];

const FAQ = [
  { q: 'What is MYARC Network?', a: 'A private startup network for founders, skilled talent, businesses, and angels to find people, ventures, capital, and opportunities with better context than cold outreach.' },
  { q: 'What is the Brain Scan?', a: 'A 60-question onboarding diagnostic that assigns your Engine and Arc based on how you build, decide, lead, and respond under pressure.' },
  { q: 'How are Arc types used?', a: 'Arc types give every profile a practical operating signal. They help people understand working style, pressure response, strengths, and potential gaps before an intro happens.' },
  { q: 'Can talent and angels join?', a: 'Yes. Founders are the first public focus, but skilled talent, businesses, and angels are part of the Network from the start.' },
  { q: 'What happens after the Brain Scan?', a: 'Your Arc profile saves to your account and you join the Network waitlist. When the platform opens, profile setup collects more context about your role, skills, venture, and goals.' },
];

/* ─────────────────────────────────────────────────────────────────
   Visual primitives: Member Card + Match notification
   ───────────────────────────────────────────────────────────── */
function MemberCard({ scrollY = 0, name = 'Marcus A.', arc = 'The Nomad', engine = 'Sovereign', role = 'Founder, Stealth fintech', looking = 'Technical cofounder', city = 'Sydney · AU' }) {
  return (
    <div style={{
      position: 'absolute',
      top: 30, left: 30, right: 30,
      borderRadius: 22,
      padding: 32,
      background: 'linear-gradient(155deg, #18130E 0%, #0B0A08 100%)',
      border: '1px solid rgba(199,164,109,0.22)',
      boxShadow: '0 60px 120px rgba(0,0,0,0.7), inset 0 1px 0 rgba(199,164,109,0.1)',
      transform: `perspective(1600px) rotateY(-8deg) rotateX(4deg) translateY(${scrollY * -0.06}px)`,
      transformOrigin: 'center center',
      transition: 'transform 0.1s linear',
    }}>
      {/* Header: M mark + member ID */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 36 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 32, color: '#C7A46D', lineHeight: 1, letterSpacing: '0.02em' }}>M</div>
        <div style={{ fontFamily: 'var(--font-label)', fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>
          MBR · 0014
        </div>
      </div>

      {/* Name + role */}
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 500, letterSpacing: '-0.005em', marginBottom: 6 }}>
        {name}
      </div>
      <div style={{ fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(199,164,109,0.75)', marginBottom: 4 }}>
        {role}
      </div>
      <div style={{ fontFamily: 'var(--font-ui)', fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 28 }}>
        {city}
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(199,164,109,0.3), transparent)', marginBottom: 24 }} />

      {/* Arc + Engine */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-label)', fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>
            Arc
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontStyle: 'italic', color: '#E8C896' }}>
            {arc}
          </div>
        </div>
        <div>
          <div style={{ fontFamily: 'var(--font-label)', fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>
            Engine
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: '#fff' }}>
            {engine}
          </div>
        </div>
      </div>

      {/* Looking for */}
      <div style={{ padding: 16, borderRadius: 12, background: 'rgba(199,164,109,0.06)', border: '1px solid rgba(199,164,109,0.18)' }}>
        <div style={{ fontFamily: 'var(--font-label)', fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(199,164,109,0.7)', marginBottom: 6 }}>
          Looking for
        </div>
        <div style={{ fontFamily: 'var(--font-ui)', fontSize: 14, color: 'rgba(255,255,255,0.9)' }}>
          {looking}
        </div>
      </div>

      {/* Footer */}
      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['◆','◈','✦'].map((g, i) => (
            <span key={i} style={{ color: 'rgba(199,164,109,0.7)', fontSize: 11 }}>{g}</span>
          ))}
        </div>
        <div style={{ fontFamily: 'var(--font-label)', fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)' }}>
          MYARC Network
        </div>
      </div>
    </div>
  );
}

function MatchNotification({ scrollY = 0 }) {
  return (
    <div className="ml-glass" style={{
      position: 'absolute',
      bottom: 20, right: 0,
      padding: 18,
      borderRadius: 16,
      width: 270,
      transform: `translateY(${scrollY * -0.03}px)`,
      transition: 'transform 0.1s linear',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #C7A46D, #7a5418)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontSize: 14, color: '#0A0807', fontWeight: 600 }}>
          ER
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 500 }}>Elena R.</div>
          <div style={{ fontFamily: 'var(--font-label)', fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(199,164,109,0.7)' }}>
            Alchemist · The Architect
          </div>
        </div>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', boxShadow: '0 0 12px rgba(29,158,117,0.6)' }} />
      </div>
      <div style={{ fontFamily: 'var(--font-ui)', fontSize: 12, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>
        Sent an intro request based on complementary Arc.
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Component
   ───────────────────────────────────────────────────────────── */
export default function ModernLanding() {
  const [audienceKey, setAudienceKey] = useState('founders');
  const [openFaq, setOpenFaq] = useState(0);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  const audience = AUDIENCES.find(a => a.key === audienceKey);

  useEffect(() => {
    api.get('/waitlist/count')
      .then(r => setWaitlistCount(Number(r.data.count || 0)))
      .catch(() => setWaitlistCount(0));
  }, []);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div style={{ background: '#070605', color: '#fff', fontFamily: 'var(--font-ui)', minHeight: '100vh', overflowX: 'hidden' }}>
      {/* ───── NAV ───── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: scrollY > 30 ? 'rgba(7,6,5,0.78)' : 'transparent',
        backdropFilter: scrollY > 30 ? 'blur(24px) saturate(140%)' : 'none',
        WebkitBackdropFilter: scrollY > 30 ? 'blur(24px) saturate(140%)' : 'none',
        borderBottom: scrollY > 30 ? '1px solid rgba(255,255,255,0.05)' : '1px solid transparent',
        transition: 'all 0.3s ease',
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '18px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
            <img src="/assets/logo.png" alt="MYARC" style={{ height: 36, width: 'auto', objectFit: 'contain' }} />
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <a href="#network"   className="ml-cta-ghost" style={{ padding: '10px 16px', fontSize: 11, border: 'none', background: 'none' }}>Network</a>
            <a href="#audiences" className="ml-cta-ghost" style={{ padding: '10px 16px', fontSize: 11, border: 'none', background: 'none' }}>For you</a>
            <a href="#faq"       className="ml-cta-ghost" style={{ padding: '10px 16px', fontSize: 11, border: 'none', background: 'none' }}>FAQ</a>
            <Link to="/login"    className="ml-cta-ghost" style={{ padding: '10px 16px', fontSize: 11, border: 'none', background: 'none' }}>Sign in</Link>
            <Link to="/assessment" className="ml-cta" style={{ padding: '12px 22px', fontSize: 11 }}>
              Brain Scan <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </nav>

      {/* ───── HERO ───── */}
      <section style={{ position: 'relative', padding: '160px 32px 120px', overflow: 'hidden' }}>
        <div className="ml-aurora-1" />
        <div className="ml-aurora-2" />
        <div className="ml-aurora-3" />
        <div className="ml-grid-bg" />
        <div className="ml-grain" />

        <div className="ml-hero-grid" style={{ position: 'relative', maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 64, alignItems: 'center' }}>
          {/* Left */}
          <div className="ml-rise">
            <div className="ml-pill" style={{ marginBottom: 28 }}>
              <span className="ml-pill-dot" />
              Private beta · First 100 verifications free
            </div>

            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(56px, 7.2vw, 104px)',
              lineHeight: 0.98,
              letterSpacing: '-0.02em',
              margin: '0 0 28px',
              fontWeight: 500,
            }}>
              Find the people<br />
              who <span className="ml-gold-text" style={{ fontStyle: 'italic' }}>think like you build.</span>
            </h1>

            <p style={{
              fontSize: 19,
              lineHeight: 1.65,
              color: 'rgba(255,255,255,0.7)',
              maxWidth: 560,
              margin: '0 0 16px',
              fontWeight: 300,
            }}>
              A private startup network for cofounders, talent, angels, and ventures with context that cold outreach can&apos;t give you.
            </p>

            <p style={{
              fontSize: 14,
              lineHeight: 1.7,
              color: 'rgba(255,255,255,0.45)',
              maxWidth: 520,
              margin: '0 0 44px',
            }}>
              Start with the Brain Scan. Your Arc profile becomes the first signal for how you build, decide, and work under pressure.
            </p>

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 56 }}>
              <Link to="/assessment" className="ml-cta">
                Create your Arc profile <ArrowRight size={16} />
              </Link>
              <a href="#how" className="ml-cta-ghost">
                See how it works
              </a>
            </div>

            {/* inline trust line */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.08em' }}>
                <Shield size={14} style={{ color: '#C7A46D' }} />
                Private by default
              </div>
              <div style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.08)' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.08em' }}>
                <Sparkles size={14} style={{ color: '#C7A46D' }} />
                Arc-verified profiles
              </div>
              <div style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.08)' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.08em' }}>
                <Lock size={14} style={{ color: '#C7A46D' }} />
                Structured intros
              </div>
            </div>
          </div>

          {/* Right — Network Member Card mockup */}
          <div className="ml-hero-visual ml-rise" style={{ position: 'relative', height: 560, animationDelay: '180ms' }}>
            <MemberCard scrollY={scrollY} />
            <MatchNotification scrollY={scrollY} />
          </div>
        </div>

        {/* scroll cue */}
        <div style={{ position: 'relative', marginTop: 80, display: 'flex', justifyContent: 'center', color: 'rgba(255,255,255,0.35)', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: 'var(--font-label)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            Scroll
            <ChevronDown size={14} style={{ animation: 'ml-pulse 2s ease-in-out infinite' }} />
          </div>
        </div>
      </section>

      {/* ───── MARQUEE / stats strip ───── */}
      <section style={{ position: 'relative', padding: '40px 0', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden', background: 'rgba(8,7,6,0.5)' }}>
        <div className="ml-marquee">
          {[...Array(2)].map((_, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 64, paddingRight: 64, flexShrink: 0 }}>
              {['ARC-VERIFIED PROFILES', '6 CORE ENGINES', '30 ARC TYPES', 'STRUCTURED INTROS', 'PRIVATE BY DEFAULT', 'NO COLD DMS', 'FOUNDER-FIRST', 'ANGEL ACCESS'].map(t => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 64, color: 'rgba(255,255,255,0.4)', fontFamily: 'var(--font-label)', fontSize: 12, letterSpacing: '0.25em' }}>
                  <span>{t}</span>
                  <span style={{ color: '#C7A46D' }}>◆</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ───── STATS BENTO ───── */}
      <section style={{ position: 'relative', padding: '120px 32px 80px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <Reveal>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap', marginBottom: 56 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#C7A46D', marginBottom: 16 }}>
                  / 01 · The signal
                </div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 1.05, margin: 0, maxWidth: 720, letterSpacing: '-0.01em' }}>
                  The profile starts <span className="ml-gold-text" style={{ fontStyle: 'italic' }}>before</span> the resume.
                </h2>
              </div>
              <p style={{ maxWidth: 380, color: 'rgba(255,255,255,0.6)', fontSize: 15, lineHeight: 1.75, margin: 0 }}>
                Skills matter. Experience matters. But how someone handles pressure, decisions, and execution matters too. The Brain Scan gives MYARC a practical starting signal.
              </p>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 80}>
                <div className="ml-card" style={{ padding: '36px 28px' }}>
                  <div className="ml-num-display ml-gold-text" style={{ fontSize: 64, lineHeight: 1, fontWeight: 400 }}>
                    <CountUp value={s.n} />
                  </div>
                  <div style={{ marginTop: 14, fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                    {s.label}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───── PRODUCT BENTO ───── */}
      <section id="network" style={{ position: 'relative', padding: '80px 32px 120px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto 64px' }}>
              <div style={{ fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#C7A46D', marginBottom: 16 }}>
                / 02 · The Network
              </div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 1.05, margin: 0, letterSpacing: '-0.01em' }}>
                Move from cold search<br />to <span className="ml-gold-text" style={{ fontStyle: 'italic' }}>warmer context.</span>
              </h2>
              <p style={{ marginTop: 24, color: 'rgba(255,255,255,0.6)', fontSize: 16, lineHeight: 1.75 }}>
                Built around venture cards, people profiles, Arc signals, skills, intent, and structured intro requests.
              </p>
            </div>
          </Reveal>

          {/* Bento */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 16 }}>
            {/* Big feature card with profile stack mockup */}
            <Reveal className="" delay={0} style={{ gridColumn: 'span 7' }}>
              <div className="ml-card" style={{ padding: 0, overflow: 'hidden', height: '100%', minHeight: 520 }}>
                <div style={{ padding: '40px 40px 0' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 100, background: 'rgba(199,164,109,0.08)', border: '1px solid rgba(199,164,109,0.2)', fontFamily: 'var(--font-label)', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#C7A46D', marginBottom: 24 }}>
                    <Sparkles size={11} /> Flagship
                  </div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 36, lineHeight: 1.1, margin: '0 0 14px', letterSpacing: '-0.01em' }}>
                    Find cofounders <span className="ml-gold-text" style={{ fontStyle: 'italic' }}>who fit.</span>
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.62)', fontSize: 15, lineHeight: 1.75, margin: 0, maxWidth: 460 }}>
                    Match on Arc, skills, and execution signal, not just headline overlap. See how each candidate actually works before the first call.
                  </p>
                </div>

                {/* Profile stack mockup */}
                <div style={{ position: 'relative', marginTop: 36, padding: '0 32px 36px', height: 280 }}>
                  {[
                    { name: 'Elena R.',  arc: 'The Architect', engine: 'Alchemist', role: 'Product · Fintech',     match: 94, offset: 0   },
                    { name: 'Daniel K.', arc: 'The Strategist',engine: 'Arbiter',   role: 'Operator · Marketplaces', match: 88, offset: 56  },
                    { name: 'Aiko T.',   arc: 'The Forger',    engine: 'Titan',     role: 'Engineer · 0→1 systems',  match: 82, offset: 112 },
                  ].map((p, i) => (
                    <div key={p.name} style={{
                      position: 'absolute',
                      top: p.offset, left: 0, right: 0,
                      padding: '16px 20px',
                      borderRadius: 14,
                      background: `linear-gradient(155deg, rgba(24,19,14,${0.96 - i*0.06}), rgba(11,10,8,${0.96 - i*0.06}))`,
                      border: '1px solid rgba(199,164,109,0.18)',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                      display: 'flex', alignItems: 'center', gap: 16,
                      zIndex: 10 - i,
                      transform: `scale(${1 - i*0.02})`,
                    }}>
                      <div style={{ width: 42, height: 42, borderRadius: 12, background: 'linear-gradient(135deg, #C7A46D, #7a5418)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontSize: 16, color: '#0A0807', fontWeight: 600, flexShrink: 0 }}>
                        {p.name.split(' ').map(s => s[0]).join('')}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                          <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 500 }}>{p.name}</div>
                          <div style={{ fontFamily: 'var(--font-label)', fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(199,164,109,0.7)' }}>
                            {p.engine}
                          </div>
                        </div>
                        <div style={{ fontFamily: 'var(--font-ui)', fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                          {p.arc} · {p.role}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div className="ml-num-display" style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: '#E8C896', lineHeight: 1 }}>
                          {p.match}<span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginLeft: 2 }}>%</span>
                        </div>
                        <div style={{ fontFamily: 'var(--font-label)', fontSize: 8, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                          Fit
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* Side stacked cards */}
            <div style={{ gridColumn: 'span 5', display: 'grid', gap: 16 }}>
              {SURFACES.slice(1).map(({ icon: Icon, label, body, tag }, i) => (
                <Reveal key={label} delay={120 + i * 80}>
                  <div className="ml-card" style={{ padding: 32 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
                      <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, rgba(199,164,109,0.15), rgba(199,164,109,0.04))', border: '1px solid rgba(199,164,109,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon size={20} color="#C7A46D" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 22, margin: 0, fontWeight: 500 }}>{label}</h3>
                          <span style={{ fontFamily: 'var(--font-label)', fontSize: 9, letterSpacing: '0.22em', color: 'rgba(199,164,109,0.6)' }}>{tag}</span>
                        </div>
                        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, lineHeight: 1.7, margin: 0 }}>{body}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───── HOW IT WORKS ───── */}
      <section id="how" style={{ position: 'relative', padding: '120px 32px', borderTop: '1px solid rgba(255,255,255,0.05)', background: 'linear-gradient(180deg, transparent, rgba(199,164,109,0.02), transparent)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 80px' }}>
              <div style={{ fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#C7A46D', marginBottom: 16 }}>
                / 03 · How it works
              </div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 1.05, margin: 0, letterSpacing: '-0.01em' }}>
                Three steps from <span className="ml-gold-text" style={{ fontStyle: 'italic' }}>diagnostic</span> to intro.
              </h2>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, position: 'relative' }}>
            {/* connecting line */}
            <div style={{ position: 'absolute', top: 50, left: '14%', right: '14%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(199,164,109,0.3), transparent)', zIndex: 0 }} />

            {STEPS.map((step, i) => (
              <Reveal key={step.n} delay={i * 120}>
                <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                  <div style={{
                    width: 100, height: 100, margin: '0 auto 28px', borderRadius: '50%',
                    background: 'radial-gradient(circle at 30% 30%, rgba(232,200,150,0.18), rgba(199,164,109,0.04))',
                    border: '1px solid rgba(199,164,109,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
                    boxShadow: '0 0 60px rgba(199,164,109,0.12), inset 0 1px 0 rgba(255,255,255,0.08)',
                  }}>
                    <step.icon size={32} color="#E8C896" />
                  </div>
                  <div className="ml-num-display" style={{ fontFamily: 'var(--font-display)', fontSize: 14, letterSpacing: '0.32em', color: '#C7A46D', marginBottom: 12 }}>
                    {step.n}
                  </div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 26, margin: '0 0 14px', fontWeight: 500, letterSpacing: '-0.01em' }}>{step.label}</h3>
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, lineHeight: 1.75, margin: '0 auto', maxWidth: 320 }}>{step.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───── AUDIENCE TABS ───── */}
      <section id="audiences" style={{ position: 'relative', padding: '120px 32px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 56px' }}>
              <div style={{ fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#C7A46D', marginBottom: 16 }}>
                / 04 · Who it&apos;s for
              </div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 1.05, margin: 0, letterSpacing: '-0.01em' }}>
                Built for the <span className="ml-gold-text" style={{ fontStyle: 'italic' }}>full venture circle.</span>
              </h2>
            </div>
          </Reveal>

          <Reveal>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 48 }}>
              {AUDIENCES.map(a => (
                <button
                  key={a.key}
                  onClick={() => setAudienceKey(a.key)}
                  className={`ml-tab ${audienceKey === a.key ? 'is-active' : ''}`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <div className="ml-card" style={{ padding: 56, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56, alignItems: 'center' }}>
              <div>
                <div style={{ width: 64, height: 64, borderRadius: 18, background: 'linear-gradient(135deg, rgba(199,164,109,0.2), rgba(199,164,109,0.04))', border: '1px solid rgba(199,164,109,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 28 }}>
                  <audience.icon size={26} color="#E8C896" />
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 3.5vw, 44px)', lineHeight: 1.1, margin: '0 0 20px', letterSpacing: '-0.01em' }}>
                  {audience.title}
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 16, lineHeight: 1.75, margin: '0 0 32px' }}>
                  {audience.body}
                </p>
                <Link to="/assessment" className="ml-cta">
                  Start your Brain Scan <ArrowRight size={16} />
                </Link>
              </div>
              <div style={{ display: 'grid', gap: 12 }}>
                {audience.bullets.map((b, i) => (
                  <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 24px', borderRadius: 14, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(199,164,109,0.1)', border: '1px solid rgba(199,164,109,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'var(--font-label)', fontSize: 11, color: '#C7A46D', fontWeight: 600 }}>
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.85)' }}>{b}</div>
                    <ArrowUpRight size={16} style={{ marginLeft: 'auto', color: 'rgba(199,164,109,0.5)' }} />
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───── SIX ENGINES ───── */}
      <section style={{ position: 'relative', padding: '120px 32px', overflow: 'hidden', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="ml-aurora-1" style={{ opacity: 0.4 }} />
        <div style={{ position: 'relative', maxWidth: 1280, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto 64px' }}>
              <div style={{ fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#C7A46D', marginBottom: 16 }}>
                / 05 · The system
              </div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 1.05, margin: 0, letterSpacing: '-0.01em' }}>
                Six Engines.<br />
                <span className="ml-gold-text" style={{ fontStyle: 'italic' }}>Thirty Arcs.</span>
              </h2>
              <p style={{ marginTop: 24, color: 'rgba(255,255,255,0.6)', fontSize: 16, lineHeight: 1.75 }}>
                Your top two Engines combine into one of thirty Arcs. Practical operating psychology, not a personality quiz.
              </p>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {ENGINES.map((e, i) => (
              <Reveal key={e.name} delay={i * 70}>
                <div className="ml-card" style={{ padding: 28, display: 'flex', alignItems: 'center', gap: 22 }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: 16,
                    background: 'radial-gradient(circle at 30% 30%, rgba(232,200,150,0.25), rgba(199,164,109,0.05))',
                    border: '1px solid rgba(199,164,109,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 26, color: '#E8C896',
                    flexShrink: 0,
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
                  }}>
                    {e.glyph}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'var(--font-label)', fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(199,164,109,0.7)', marginBottom: 4 }}>
                      Engine {String(i + 1).padStart(2, '0')}
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 500, marginBottom: 4, letterSpacing: '-0.005em' }}>
                      {e.name}
                    </div>
                    <div style={{ fontFamily: 'var(--font-ui)', fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
                      {e.trait}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={200}>
            <div style={{ textAlign: 'center', marginTop: 48 }}>
              <Link to="/assessment" className="ml-cta-ghost">
                Discover your Arc <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───── FAQ ───── */}
      <section id="faq" style={{ position: 'relative', padding: '120px 32px' }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 64 }}>
              <div style={{ fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#C7A46D', marginBottom: 16 }}>
                / 06 · Questions
              </div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 60px)', lineHeight: 1.05, margin: 0, letterSpacing: '-0.01em' }}>
                Questions before <span className="ml-gold-text" style={{ fontStyle: 'italic' }}>you enter.</span>
              </h2>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gap: 8 }}>
            {FAQ.map((item, i) => (
              <Reveal key={item.q} delay={i * 60}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="ml-card"
                  style={{ width: '100%', padding: 28, textAlign: 'left', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.06)', color: '#fff', font: 'inherit' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24 }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 500 }}>{item.q}</span>
                    <span style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(199,164,109,0.08)', border: '1px solid rgba(199,164,109,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#C7A46D', transition: 'transform 0.3s', transform: openFaq === i ? 'rotate(45deg)' : 'none' }}>
                      <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
                    </span>
                  </div>
                  <div style={{ maxHeight: openFaq === i ? 200 : 0, overflow: 'hidden', transition: 'max-height 0.4s cubic-bezier(0.22,1,0.36,1), margin 0.4s, opacity 0.4s', opacity: openFaq === i ? 1 : 0, marginTop: openFaq === i ? 16 : 0 }}>
                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 15, lineHeight: 1.75, margin: 0 }}>{item.a}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───── FINAL CTA ───── */}
      <section style={{ position: 'relative', padding: '40px 32px 120px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <Reveal>
            <div className="ml-card" style={{ padding: 80, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 0%, rgba(199,164,109,0.12), transparent 60%)', pointerEvents: 'none' }} />
              <div style={{ position: 'relative' }}>
                <div className="ml-pill" style={{ marginBottom: 32 }}>
                  <span className="ml-pill-dot" />
                  {waitlistCount > 0 ? `${waitlistCount.toLocaleString()} on the waitlist` : 'Reserve your place'}
                </div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(40px, 6vw, 80px)', lineHeight: 1, margin: '0 0 24px', letterSpacing: '-0.02em' }}>
                  Find your Arc.<br />
                  <span className="ml-gold-text" style={{ fontStyle: 'italic' }}>Then find your people.</span>
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 17, lineHeight: 1.7, margin: '0 auto 40px', maxWidth: 540 }}>
                  Take the Brain Scan, save your Arc profile, and join the MYARC Network waitlist.
                </p>
                <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link to="/assessment" className="ml-cta">
                    Create your Arc profile <ArrowRight size={16} />
                  </Link>
                  <Link to="/login" className="ml-cta-ghost">
                    I have an account
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───── FOOTER ───── */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.05)', padding: '60px 32px 40px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 24 }}>
          <img src="/assets/logo.png" alt="MYARC" style={{ height: 36, opacity: 0.7 }} />
          <div style={{ display: 'flex', gap: 32, fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)' }}>
            <a href="#network" style={{ color: 'inherit', textDecoration: 'none' }}>Network</a>
            <a href="#how" style={{ color: 'inherit', textDecoration: 'none' }}>How it works</a>
            <a href="#faq" style={{ color: 'inherit', textDecoration: 'none' }}>FAQ</a>
            <Link to="/login" style={{ color: 'inherit', textDecoration: 'none' }}>Sign in</Link>
          </div>
          <div style={{ fontFamily: 'var(--font-label)', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)' }}>
            2026 MYARC · Arc-based startup discovery
          </div>
        </div>
      </footer>
    </div>
  );
}
